import { useState, useEffect, useRef } from 'react';
import {
  useApi,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  LinearProgress,
  IconButton,
  Checkbox,
  Tooltip,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import LinkIcon from '@material-ui/icons/Link';
import SpeedIcon from '@material-ui/icons/Speed';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { ItemType, MarketplaceItem, ScannedItem } from './types';
import { COMING_SOON_TYPES, TYPE_META, isComingSoonType } from './typeMetadata';
import { TypeIcon } from './TypeIcon';
import { useMarketplaceFetchApi } from './marketplaceFetch';

type Phase =
  | 'step1'
  | 'step2'
  | 'step2b'
  | 'step3'
  | 'submitting'
  | 'success'
  | 'error';

interface ContributeDialogProps {
  open: boolean;
  onClose: () => void;
  editItem?: MarketplaceItem;
}

const TYPES = Object.keys(TYPE_META) as ItemType[];
const NON_SCANNABLE_TYPES = COMING_SOON_TYPES;
const NAME_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
const RESERVED_SLUGS = new Set(['item', 'test', 'example', 'new', 'default', 'undefined', 'null']);

function normalizeSlug(value: string): string {
  let slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!slug) return '';
  if (!/^[a-z0-9]/.test(slug)) return '';
  slug = slug.replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  if (slug.length > 63) slug = slug.slice(0, 63).replace(/-+$/g, '');
  if (!slug || !/^[a-z0-9]/.test(slug)) return '';

  return slug;
}

function slugToDisplayName(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 18,
      maxWidth: 720,
      width: '100%',
      overflow: 'hidden',
      background: theme.palette.background.paper,
    },
  },
  banner: {
    padding: theme.spacing(2.5, 3, 2),
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
  },
  bannerText: {
    flex: 1,
  },
  bannerTitle: {
    fontWeight: 800,
    fontSize: '1.15rem',
    color: '#fff',
  },
  bannerStep: {
    fontSize: '0.78rem',
    color: 'rgba(255,255,255,0.75)',
    marginTop: theme.spacing(0.25),
  },
  progressBar: {
    height: 3,
    borderRadius: 0,
    background: 'rgba(255,255,255,0.2)',
    '& .MuiLinearProgress-bar': {
      background: 'rgba(255,255,255,0.85)',
    },
  },
  body: {
    padding: theme.spacing(3.5, 4, 1),
  },
  actions: {
    padding: theme.spacing(2.5, 4, 3),
    display: 'flex',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2.5),
    position: 'relative' as const,
    zIndex: 1,
  },
  // Step 1 — type grid
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(2.5),
    '@media (max-width: 600px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  typeBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.4),
    padding: theme.spacing(1.2, 0.5),
    borderRadius: 10,
    border: `1.5px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    cursor: 'pointer',
    transition: 'border 0.15s, background 0.15s, transform 0.1s',
    '&:hover': { transform: 'translateY(-1px)' },
  },
  typeBtnDisabled: {
    cursor: 'not-allowed',
    opacity: 0.55,
    '&:hover': { transform: 'none' },
  },
  typeBtnActive: {
    border: '1.5px solid transparent',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
  },
  typeBtnEmoji: {
    fontSize: '1.3rem',
    lineHeight: 1,
  },
  typeBtnLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    lineHeight: 1,
  },
  comingSoonBadge: {
    marginTop: theme.spacing(0.35),
    fontSize: '0.64rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    opacity: 0.72,
  },
  slugPreview: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  // Step 2 — fields
  fieldLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
    marginTop: theme.spacing(2),
    '&:first-child': { marginTop: 0 },
  },
  field: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      fontSize: '0.88rem',
    },
  },
  helperText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.4,
  },
  charCount: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    textAlign: 'right' as const,
    marginTop: theme.spacing(0.3),
  },
  // Toggle
  tabToggle: {
    display: 'flex',
    borderRadius: 10,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2.5),
    marginTop: theme.spacing(2),
  },
  tabBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    border: 'none',
    background: theme.palette.background.default,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
    transition: 'all 0.15s',
    '&:hover': { background: theme.palette.action.hover },
  },
  tabBtnActive: {
    background: theme.palette.primary.main,
    color: '#fff',
  },
  // Drop zone
  dropZone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
    background: theme.palette.background.default,
  },
  dropZoneActive: {
    borderColor: theme.palette.primary.main,
    background: 'rgba(0,80,179,0.04)',
  },
  // Keywords
  keywordsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    minHeight: 44,
    alignItems: 'center',
    cursor: 'text',
    background: theme.palette.background.default,
    '&:focus-within': { borderColor: theme.palette.primary.main },
  },
  keywordChip: {
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  keywordInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 80,
    padding: '2px 4px',
    fontFamily: 'inherit',
  },
  // Review card
  reviewCard: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  },
  reviewCardHeader: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  reviewCardBody: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(0,0,0,0.15)' : '#F8FAFC',
  },
  reviewFiles: {
    background: theme.palette.type === 'dark' ? 'rgba(0,0,0,0.2)' : '#F3F5F8',
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    lineHeight: 1.8,
    marginTop: theme.spacing(1),
  },
  reviewRow: {
    display: 'flex',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
    fontSize: '0.82rem',
  },
  reviewLabel: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    minWidth: 90,
    flexShrink: 0,
  },
  reviewKeywords: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  // Token efficiency panel
  tePanel: {
    marginTop: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  },
  tePanelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.5, 2),
  },
  tePanelHeaderPass: {
    background: 'rgba(9,167,97,0.06)',
    borderBottom: '1px solid rgba(9,167,97,0.15)',
  },
  tePanelHeaderFail: {
    background: 'rgba(211,47,47,0.04)',
    borderBottom: '1px solid rgba(211,47,47,0.12)',
  },
  tePanelHeaderLoading: {
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : '#F8FAFC',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tePanelBody: {
    padding: theme.spacing(1.75, 2),
    background: theme.palette.type === 'dark' ? 'rgba(0,0,0,0.12)' : '#F8FAFC',
  },
  teScoreArea: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.75),
    paddingBottom: theme.spacing(1.75),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  teScoreCircle: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: '2px solid',
  },
  teScoreNumber: {
    fontWeight: 800,
    fontSize: '1.1rem',
    lineHeight: 1,
  },
  teScoreLabel: {
    fontSize: '0.58rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    lineHeight: 1,
    marginTop: 1,
  },
  teMetricGrid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: theme.spacing(0.5),
  },
  teMetricCell: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: theme.spacing(0.6, 0),
    borderRadius: 6,
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  },
  teMetricValue: {
    fontWeight: 700,
    fontSize: '0.88rem',
    lineHeight: 1,
  },
  teMetricName: {
    fontSize: '0.6rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    lineHeight: 1,
  },
  teDivider: {
    height: 1,
    background: theme.palette.divider,
    margin: theme.spacing(1.25, 0),
  },
  teListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    },
  },
  teListText: {
    fontSize: '0.8rem',
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  },
  teListSubText: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  teSectionTitle: {
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.07em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
  },
  teFooterNote: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.875, 1.25),
    borderRadius: 8,
    fontSize: '0.75rem',
    lineHeight: 1.4,
  },
  teFooterPass: {
    background: 'rgba(9,167,97,0.08)',
    color: '#09A761',
    border: '1px solid rgba(9,167,97,0.18)',
  },
  teFooterWarn: {
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
  },
  // Footer buttons
  cancelBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 10,
    fontSize: '0.85rem',
    boxShadow: 'none',
    color: theme.palette.text.secondary,
  },
  primaryBtn: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
    fontSize: '0.85rem',
    boxShadow: 'none',
  },
}));

export function ContributeDialog({ open, onClose, editItem }: ContributeDialogProps) {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useMarketplaceFetchApi();

  const isEditMode = !!editItem;

  const [phase, setPhase] = useState<Phase>(isEditMode ? 'step2' : 'step1');
  const [selectedType, setSelectedType] = useState<ItemType>(editItem?.type ?? 'agent');
  const [displayName, setDisplayName] = useState(editItem?.displayName ?? '');

  const [contentMethod, setContentMethod] = useState<'upload' | 'link'>(isEditMode ? 'upload' : 'link');
  const [repoUrl, setRepoUrl] = useState('');
  const [description, setDescription] = useState(editItem?.description ?? '');
  const [keywords, setKeywords] = useState<string[]>(editItem?.keywords ?? []);
  const [version, setVersion] = useState(() => {
    if (!editItem?.version) return '1.0.0';
    const parts = editItem.version.split('.');
    if (parts.length === 3) {
      parts[2] = String(Number(parts[2]) + 1);
      return parts.join('.');
    }
    return editItem.version;
  });

  const [uploadedContent, setUploadedContent] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [scanError, setScanError] = useState('');
  const [scanFilter, setScanFilter] = useState('');
  const [keywordDraft, setKeywordDraft] = useState('');
  const [prUrl, setPrUrl] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const lastScannedUrl = useRef('');

  // Token efficiency analysis state
  const [tokenAnalysis, setTokenAnalysis] = useState<{
    score: number;
    isTokenEfficient: boolean;
    promptTokens: number;
    issues: string[];
    suggestions: string[];
    breakdown: { promptScore: number; outputConstraintScore: number; redundancyScore: number; structureScore: number; focusScore: number };
  } | null>(null);
  const [tokenAnalysisLoading, setTokenAnalysisLoading] = useState(false);
  const [tokenAnalysisSkipped, setTokenAnalysisSkipped] = useState(false);
  const [tokenAnalysisError, setTokenAnalysisError] = useState(false);

  const resetDraftState = () => {
    setDisplayName('');
    setContentMethod('link');
    setRepoUrl('');
    setDescription('');
    setKeywords([]);
    setVersion('1.0.0');
    setUploadedContent('');
    setUploadedFileName('');
    setScanning(false);
    setScannedItems([]);
    setSelectedItems(new Set());
    setScanError('');
    setScanFilter('');
    setKeywordDraft('');
    lastScannedUrl.current = '';
    setTokenAnalysis(null);
    setTokenAnalysisLoading(false);
    setTokenAnalysisSkipped(false);
    setTokenAnalysisError(false);
  };

  const handleTypeSelect = (nextType: ItemType) => {
    if (isComingSoonType(nextType) || nextType === selectedType) return;
    setSelectedType(nextType);
    resetDraftState();
  };

  const resetAndClose = () => {
    onClose();
    setTimeout(() => {
      setPhase('step1');
      setSelectedType('agent');
      resetDraftState();
      setPrUrl('');
      setSubmitError('');
      setShowHelp(false);
    }, 200);
  };

  const isScannableType = !NON_SCANNABLE_TYPES.has(selectedType);
  const hasValidRepoUrl = repoUrl.startsWith('https://github.com/');
  const selectedScannedItems = scannedItems.filter((_, idx) =>
    selectedItems.has(idx),
  );
  const filteredScannedItems = scannedItems
    .map((item, idx) => ({ item, originalIndex: idx }))
    .filter(
      ({ item }) =>
        item.suggestedDisplayName
          .toLowerCase()
          .includes(scanFilter.toLowerCase().trim()) || !scanFilter.trim(),
    );
  const hasScannedResults = scannedItems.length > 0;
  const isScanSelectionMode = contentMethod === 'link' && hasScannedResults;

  const handleScanRepo = async () => {
    if (!hasValidRepoUrl || !isScannableType) return;
    setScanning(true);
    setScanError('');
    setScanFilter('');
    setScannedItems([]);
    setSelectedItems(new Set());
    try {
      const baseUrl = await discoveryApi.getBaseUrl('marketplace');
      const resp = await fetchApi.fetch(`${baseUrl}/scan-repo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ repoUrl, type: selectedType }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error ?? `HTTP ${resp.status}`);
      const items: ScannedItem[] = Array.isArray(json.items) ? json.items : [];
      setScannedItems(items);
      setSelectedItems(new Set(items.length > 0 ? [0] : []));
      if (items.length === 0) {
        setScanError(
          typeof json.hint === 'string' && json.hint.trim()
            ? json.hint
            : `No ${TYPE_META[selectedType].label}s found in this repository.`,
        );
        setPhase('step2');
        return;
      }
    } catch (err: any) {
      setScanError(err.message ?? 'Failed to scan repository');
    } finally {
      setScanning(false);
    }
  };

  const updateScannedItem = (idx: number, patch: Partial<ScannedItem>) => {
    setScannedItems(prev =>
      prev.map((item, i) => (i === idx ? { ...item, ...patch } : item)),
    );
  };

  const toggleSelectedItem = (idx: number) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Auto-trigger scan when entering step2b, guarded to avoid redundant scans
  useEffect(() => {
    if (phase === 'step2b' && repoUrl !== lastScannedUrl.current) {
      lastScannedUrl.current = repoUrl;
      handleScanRepo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Auto-trigger token efficiency analysis when entering step3
  useEffect(() => {
    if (phase !== 'step3') return;
    const content = isScanSelectionMode
      ? selectedScannedItems[0]?.rawContent ?? ''
      : contentMethod === 'upload'
        ? uploadedContent
        : '';
    if (!content.trim()) {
      setTokenAnalysis(null);
      setTokenAnalysisSkipped(true);
      return;
    }
    let cancelled = false;
    setTokenAnalysisLoading(true);
    setTokenAnalysis(null);
    setTokenAnalysisSkipped(false);
    setTokenAnalysisError(false);
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('marketplace');
        const resp = await window.fetch(`${baseUrl}/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content, type: selectedType }),
        });
        if (!cancelled) {
          if (resp.ok) {
            const json = await resp.json();
            if (json.skipped) {
              setTokenAnalysisSkipped(true);
            } else {
              setTokenAnalysis(json);
            }
          } else {
            setTokenAnalysisError(true);
          }
        }
      } catch {
        // Non-critical — don't block the contribution flow
        if (!cancelled) setTokenAnalysisError(true);
      } finally {
        if (!cancelled) setTokenAnalysisLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const handleSubmit = async () => {
    setPhase('submitting');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('marketplace');

      if (isEditMode) {
        const body: Record<string, any> = {
          id: editItem!.id,
          type: selectedType,
          displayName,
          description,
          version,
          keywords,
        };
        if (uploadedContent.trim()) body.content = uploadedContent;
        const resp = await fetchApi.fetch(`${baseUrl}/edit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await resp.json();
        if (!resp.ok) throw new Error(json.error ?? `HTTP ${resp.status}`);
        setPrUrl(json.prUrl);
        setPhase('success');
        return;
      }

      const body: Record<string, any> = isScanSelectionMode
        ? {
            type: selectedType,
            repoUrl,
            items: selectedScannedItems.map(item => ({
              name: item.suggestedSlug,
              displayName: item.suggestedDisplayName,
              description: item.description,
              keywords: item.keywords,
              version: item.version,
              content: item.rawContent,
              sourcePath: item.path,
            })),
          }
        : {
            type: selectedType,
            name: manualDerivedName,
            displayName: manualDerivedDisplayName,
            description,
            version,
            keywords,
            contentMethod,
          };
      if (!isScanSelectionMode) {
        if (contentMethod === 'upload') body.content = uploadedContent;
        else body.repoUrl = repoUrl;
      }
      const resp = await fetchApi.fetch(`${baseUrl}/contribute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error ?? `HTTP ${resp.status}`);
      setPrUrl(json.prUrl);
      setPhase('success');
    } catch (err: any) {
      setSubmitError(err.message ?? 'Unknown error');
      setPhase('error');
    }
  };

  const descriptionValid = description.trim().length > 0;
  const hasScanStep = isEditMode ? false : contentMethod === 'link' && isScannableType;
  const step2Valid = isEditMode
    ? descriptionValid && displayName.trim().length >= 2
    : contentMethod === 'upload'
      ? uploadedContent.trim().length > 0 && descriptionValid
      : hasValidRepoUrl && descriptionValid;
  const step2bValid = isScanSelectionMode
    ? selectedScannedItems.length > 0 &&
      selectedScannedItems.every(
        item => item.suggestedDisplayName.trim().length > 0,
      ) &&
      selectedScannedItems.every(
        item => item.suggestedSlug.trim().length > 0,
      ) &&
      selectedScannedItems.every(item =>
        NAME_RE.test(item.suggestedSlug.trim()),
      ) &&
      selectedScannedItems.every(item =>
        !RESERVED_SLUGS.has(item.suggestedSlug.trim().toLowerCase()),
      ) &&
      new Set(
        selectedScannedItems.map(i =>
          i.suggestedDisplayName.trim().toLowerCase(),
        ),
      ).size === selectedScannedItems.length &&
      new Set(
        selectedScannedItems.map(i => i.suggestedSlug.trim().toLowerCase()),
      ).size === selectedScannedItems.length
    : descriptionValid;
  const canReview = hasScanStep ? step2bValid : step2Valid;

  const slugPreview = displayName
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');
  const repoFallbackName = normalizeSlug(
    repoUrl
      .replace(/\.git$/i, '')
      .split('/')
      .filter(Boolean)
      .slice(-1)[0] ?? '',
  );
  const manualDerivedName = slugPreview || repoFallbackName;
  const manualDerivedDisplayName =
    displayName.trim() || slugToDisplayName(manualDerivedName);
  const totalSteps = hasScanStep ? 4 : 3;
  const progressValue =
    phase === 'step1'
      ? hasScanStep
        ? 25
        : 33
      : phase === 'step2'
        ? hasScanStep
          ? 50
          : 66
        : phase === 'step2b'
          ? 75
          : 100;
  const phaseAction =
    phase === 'step1'
      ? 'Choose Type'
      : phase === 'step2'
        ? 'Add Content'
        : phase === 'step2b'
          ? 'Scan & Select'
          : 'Review';
  const currentStep =
    phase === 'step1'
      ? 1
      : phase === 'step2'
        ? 2
        : phase === 'step2b'
          ? 3
          : hasScanStep
            ? 4
            : 3;
  const stepNum = `Step ${currentStep} of ${totalSteps}`;

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && keywordDraft.trim()) {
      e.preventDefault();
      const next = keywordDraft.trim().replace(/,$/, '');
      if (next && !keywords.includes(next)) {
        setKeywords(prev => [...prev, next]);
      }
      setKeywordDraft('');
    } else if (e.key === 'Backspace' && !keywordDraft && keywords.length) {
      setKeywords(prev => prev.slice(0, -1));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFileName(file.name);
    const reader = new FileReader();
    reader.onload = ev =>
      setUploadedContent((ev.target?.result as string) ?? '');
    reader.readAsText(file);
  };

  const selectedMeta = TYPE_META[selectedType];
  const showGlobalMetadataFields =
    contentMethod === 'upload' || !hasScannedResults;

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      {/* Banner */}
      <Box
        className={classes.banner}
        style={{ background: selectedMeta.gradient }}
      >
        <Box className={classes.bannerText}>
          <DialogTitle style={{ padding: 0 }}>
            <Typography className={classes.bannerTitle}>
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <TypeIcon
                  iconName={selectedMeta.iconName}
                  style={{ color: '#fff' }}
                  fontSize="small"
                />
                &nbsp;{isEditMode ? `Edit ${selectedMeta.label}` : `${selectedMeta.label} — ${phaseAction}`}
              </span>
            </Typography>
          </DialogTitle>
          <Typography className={classes.bannerStep}>{isEditMode ? 'Edit mode' : stepNum}</Typography>
        </Box>
        <Box style={{ display: 'flex', gap: 4 }}>
          <Tooltip title="Contribution guide" arrow>
            <IconButton
              size="small"
              onClick={() => setShowHelp(h => !h)}
              style={{
                color: 'rgba(255,255,255,0.85)',
                marginTop: 2,
                background: showHelp ? 'rgba(255,255,255,0.22)' : undefined,
              }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close" arrow>
            <IconButton
              size="small"
              onClick={resetAndClose}
              style={{ color: 'rgba(255,255,255,0.85)', marginTop: 2 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progressValue}
        className={classes.progressBar}
      />

      {showHelp && (
        <Box
          style={{
            padding: '12px 24px 16px',
            background: '#F8F9FA',
            borderBottom: '1px solid #E0E0E0',
          }}
        >
          <Typography
            style={{
              fontWeight: 700,
              fontSize: '0.82rem',
              color: '#333',
              marginBottom: 4,
            }}
          >
            How to contribute
          </Typography>
          <Typography
            style={{
              fontSize: '0.75rem',
              color: '#666',
              marginBottom: 12,
              lineHeight: 1.6,
            }}
          >
            Each item lives in its own folder under{' '}
            <code
              style={{
                background: '#EBEBEB',
                padding: '1px 5px',
                borderRadius: 4,
                fontSize: '0.72rem',
              }}
            >
              content/&lt;type&gt;/&lt;your-slug&gt;/
            </code>
            . Include a <strong>manifest.json</strong> with metadata, and the
            content file(s) listed below.
          </Typography>
          <Box
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}
          >
            {[
              {
                type: 'Agent',
                path: 'agents/',
                files: 'manifest.json, README.md, {slug}.agent.md',
              },
              {
                type: 'Skill',
                path: 'skills/',
                files: 'manifest.json, README.md, SKILL.md',
              },
              {
                type: 'Prompt',
                path: 'prompts/',
                files: 'manifest.json, {slug}.prompt.md',
              },
              {
                type: 'Rule',
                path: 'rules/',
                files: 'manifest.json, {slug}.rule.md',
              },
              {
                type: 'MCP Server',
                path: 'mcp-servers/',
                files: 'manifest.json, README.md',
              },
              {
                type: 'Model',
                path: 'models/',
                files: 'manifest.json, README.md',
              },
              {
                type: 'Playbook',
                path: 'playbooks/',
                files: 'manifest.json, README.md',
              },
              {
                type: 'Template',
                path: 'templates/',
                files: 'manifest.json, README.md',
              },
            ].map(({ type, path, files }) => (
              <Box
                key={type}
                style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: 10,
                  border: '1px solid #E0E0E0',
                }}
              >
                <Typography
                  style={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#333',
                    marginBottom: 2,
                  }}
                >
                  {type}
                </Typography>
                <Typography
                  style={{
                    fontSize: '0.65rem',
                    color: '#666',
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontFamily: 'monospace', color: '#007AFF' }}>
                    {path}
                  </span>
                  <br />
                  {files}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Body */}
      <DialogContent className={classes.body}>
        {/* ── Step 1: Item basics ── */}
        {phase === 'step1' && (
          <>
            <Typography className={classes.fieldLabel} style={{ marginTop: 0 }}>
              Item type
            </Typography>
            <Box className={classes.typeGrid}>
              {TYPES.map(t => {
                const meta = TYPE_META[t];
                const isDisabled = isComingSoonType(t);
                const isActive = selectedType === t;
                return (
                  <Box
                    key={t}
                    className={`${classes.typeBtn} ${isActive ? classes.typeBtnActive : ''} ${isDisabled ? classes.typeBtnDisabled : ''}`}
                    style={
                      isActive
                        ? { background: meta.gradient, color: '#fff' }
                        : undefined
                    }
                    onClick={() => handleTypeSelect(t)}
                    aria-disabled={isDisabled}
                  >
                    <span className={classes.typeBtnEmoji}>
                      <TypeIcon iconName={meta.iconName} fontSize="small" />
                    </span>
                    <span className={classes.typeBtnLabel}>{meta.label}</span>
                    {isDisabled && (
                      <span className={classes.comingSoonBadge}>
                        Coming soon
                      </span>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Typography className={classes.helperText}>
              Artifact types marked Coming soon are visible in the catalog but
              not yet open for contribution.
            </Typography>

            <Typography className={classes.fieldLabel}>
              Display name
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="e.g. Code Review Agent"
              className={classes.field}
            />
            {slugPreview && (
              <Typography className={classes.slugPreview}>
                slug: <strong>{slugPreview}</strong>
                {RESERVED_SLUGS.has(slugPreview) && (
                  <span style={{ color: '#D97706', marginLeft: 8 }}>
                    (reserved name — please choose a different name)
                  </span>
                )}
              </Typography>
            )}
          </>
        )}

        {/* ── Step 2: Content source ── */}
        {phase === 'step2' && (
          <>
            {!isEditMode && (
            <Box className={classes.tabToggle}>
              <button
                className={classes.tabBtn}
                style={
                  contentMethod === 'upload'
                    ? { background: selectedMeta.color, color: '#fff' }
                    : undefined
                }
                onClick={() => setContentMethod('upload')}
              >
                <CloudUploadIcon fontSize="small" />
                Upload File
              </button>
              <button
                className={classes.tabBtn}
                style={
                  contentMethod === 'link'
                    ? { background: selectedMeta.color, color: '#fff' }
                    : undefined
                }
                onClick={() => setContentMethod('link')}
              >
                <LinkIcon fontSize="small" />
                Link Repository
              </button>
            </Box>
            )}

            {isEditMode && (
              <>
                <Typography className={classes.fieldLabel} style={{ marginTop: 0 }}>
                  Display name
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="e.g. Code Review Agent"
                  className={classes.field}
                />
              </>
            )}

            {contentMethod === 'link' && (
              <>
                <Typography
                  className={classes.fieldLabel}
                  style={{ marginTop: 0 }}
                >
                  GitHub repository URL
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={repoUrl}
                  onChange={e => {
                    const next = e.target.value;
                    setRepoUrl(next);
                    setScanError('');
                    lastScannedUrl.current = '';
                    if (next !== repoUrl && scannedItems.length) {
                      setScannedItems([]);
                      setSelectedItems(new Set());
                    }
                  }}
                  placeholder="https://github.com/org/repo"
                  className={classes.field}
                  error={
                    repoUrl.length > 0 &&
                    !repoUrl.startsWith('https://github.com/')
                  }
                  helperText={
                    repoUrl.length > 0 &&
                    !repoUrl.startsWith('https://github.com/')
                      ? 'URL must be a valid GitHub repository link (https://github.com/...)'
                      : undefined
                  }
                />
                <Typography className={classes.helperText}>
                  Link to a GitHub repository, branch, folder, or file that
                  contains this item. Full URLs like{' '}
                  <code>
                    .../tree/dev/.github/skills/feature-builder/SKILL.md
                  </code>{' '}
                  are supported.
                </Typography>

                {scanError && (
                  <Typography
                    style={{
                      color: '#c0392b',
                      fontSize: '0.85rem',
                      marginTop: 4,
                    }}
                  >
                    {scanError}
                  </Typography>
                )}

                {!isScannableType && (
                  <Typography className={classes.helperText}>
                    Scanning is not available for this type. Enter metadata
                    below.
                  </Typography>
                )}
              </>
            )}

            {contentMethod === 'upload' && (
              <>
                <Typography
                  className={classes.fieldLabel}
                  style={{ marginTop: 0 }}
                >
                  Upload your content file (.md)
                </Typography>
                <label>
                  <Box
                    className={`${classes.dropZone} ${uploadedFileName ? classes.dropZoneActive : ''}`}
                    component="span"
                    display="flex"
                  >
                    <CloudUploadIcon
                      style={{
                        fontSize: 32,
                        color: uploadedFileName
                          ? selectedMeta.color
                          : undefined,
                        opacity: uploadedFileName ? 1 : 0.4,
                      }}
                    />
                    {uploadedFileName ? (
                      <>
                        <Typography
                          style={{
                            fontSize: '0.88rem',
                            fontWeight: 700,
                            color: selectedMeta.color,
                          }}
                        >
                          {uploadedFileName}
                        </Typography>
                        <Typography
                          style={{ fontSize: '0.78rem', opacity: 0.6 }}
                        >
                          {uploadedContent.length} characters — click to replace
                        </Typography>
                      </>
                    ) : (
                      <Typography style={{ fontSize: '0.85rem', opacity: 0.6 }}>
                        Click to select a .md file
                      </Typography>
                    )}
                  </Box>
                  <input
                    type="file"
                    accept=".md,.txt"
                    hidden
                    onChange={handleFileChange}
                  />
                </label>
              </>
            )}

            {showGlobalMetadataFields && !hasScanStep && (
              <>
                <Typography className={classes.fieldLabel}>
                  Description
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="What does this item do?"
                  error={!descriptionValid && description.length > 0}
                  className={classes.field}
                />
                <Typography className={classes.charCount}>
                  {description.length}/200 (aim for 80–120)
                </Typography>
                {!descriptionValid && (
                  <Typography
                    className={classes.helperText}
                    style={{ color: '#c0392b' }}
                  >
                    Description is required.
                  </Typography>
                )}

                <Typography className={classes.fieldLabel}>Keywords</Typography>
                <Box className={classes.keywordsWrap}>
                  {keywords.map(kw => (
                    <Chip
                      key={kw}
                      label={kw}
                      size="small"
                      className={classes.keywordChip}
                      onDelete={() =>
                        setKeywords(prev => prev.filter(k => k !== kw))
                      }
                    />
                  ))}
                  <input
                    className={classes.keywordInput}
                    value={keywordDraft}
                    onChange={e => setKeywordDraft(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    placeholder={
                      keywords.length === 0
                        ? 'Type keyword and press Enter'
                        : ''
                    }
                  />
                </Box>

                <Typography className={classes.fieldLabel}>Version</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  value={version}
                  onChange={e => setVersion(e.target.value)}
                  placeholder="1.0.0"
                  className={classes.field}
                  style={{ width: 140 }}
                />
              </>
            )}
          </>
        )}

        {/* ── Step 2b: Scan & Select ── */}
        {phase === 'step2b' && (
          <>
            {scanning && (
              <Box style={{ marginTop: 10 }}>
                <LinearProgress />
                <Typography className={classes.helperText}>
                  Scanning repository for {TYPE_META[selectedType].label}s...
                </Typography>
              </Box>
            )}

            {scanError && (
              <Box style={{ marginTop: 8 }}>
                <Typography
                  className={classes.helperText}
                  style={{ color: '#c0392b' }}
                >
                  {scanError}
                </Typography>
                {!scanError.startsWith('No ') && (
                  <Typography
                    className={classes.helperText}
                    style={{ color: '#666', marginTop: 4 }}
                  >
                    Check that the URL is correct and the repository is visible
                    to the portal's GitHub integration.
                  </Typography>
                )}
                <Box style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPhase('step2')}
                  >
                    ← Edit URL
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setScanError('')}
                  >
                    Continue manually
                  </Button>
                </Box>
              </Box>
            )}

            {hasScannedResults && (
              <Box style={{ marginTop: 16 }}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={scanFilter}
                  onChange={e => setScanFilter(e.target.value)}
                  placeholder="Search by name..."
                  style={{ marginBottom: 14 }}
                />
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 16,
                    paddingBottom: 12,
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={
                      filteredScannedItems.length > 0 &&
                      filteredScannedItems.every(({ originalIndex }) =>
                        selectedItems.has(originalIndex),
                      )
                    }
                    indeterminate={
                      filteredScannedItems.some(({ originalIndex }) =>
                        selectedItems.has(originalIndex),
                      ) &&
                      !filteredScannedItems.every(({ originalIndex }) =>
                        selectedItems.has(originalIndex),
                      )
                    }
                    onChange={(_, checked) =>
                      setSelectedItems(prev => {
                        const next = new Set(prev);
                        filteredScannedItems.forEach(({ originalIndex }) =>
                          checked
                            ? next.add(originalIndex)
                            : next.delete(originalIndex),
                        );
                        return next;
                      })
                    }
                    color="primary"
                    style={{ padding: 4 }}
                  />
                  <Typography style={{ fontSize: '0.85rem', color: '#555' }}>
                    {(() => {
                      const selectedVisible = filteredScannedItems.filter(
                        ({ originalIndex }) => selectedItems.has(originalIndex),
                      ).length;
                      if (selectedVisible === filteredScannedItems.length)
                        return `All ${selectedVisible} selected`;
                      if (selectedVisible === 0) return 'None selected';
                      return `${selectedVisible} of ${filteredScannedItems.length} selected`;
                    })()}
                  </Typography>
                  {selectedItems.size > 0 && (
                    <Typography
                      component="span"
                      style={{
                        fontSize: '0.85rem',
                        color: '#1976d2',
                        cursor: 'pointer',
                        marginLeft: 4,
                      }}
                      onClick={() => setSelectedItems(new Set())}
                    >
                      · Clear
                    </Typography>
                  )}
                </Box>

                {filteredScannedItems.map(({ item, originalIndex }) => {
                  const selected = selectedItems.has(originalIndex);
                  return (
                    <Box
                      key={`${item.path}-${originalIndex}`}
                      style={{
                        border: '1px solid #dfe3e8',
                        borderRadius: 12,
                        padding: '18px 20px',
                        marginBottom: 16,
                      }}
                    >
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <Checkbox
                          checked={selected}
                          onChange={() => toggleSelectedItem(originalIndex)}
                          color="primary"
                          style={{ padding: 6 }}
                        />
                        <Box>
                          <Typography
                            style={{
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <TypeIcon
                              iconName={selectedMeta.iconName}
                              fontSize="small"
                              style={{ color: selectedMeta.color }}
                            />{' '}
                            {item.suggestedDisplayName}
                          </Typography>
                          <Typography
                            className={classes.helperText}
                            style={{ marginTop: 2 }}
                          >
                            {item.path}
                          </Typography>
                        </Box>
                      </Box>
                      {selected &&
                        (() => {
                          const nameKey = item.suggestedDisplayName
                            .trim()
                            .toLowerCase();
                          const slugKey = item.suggestedSlug
                            .trim()
                            .toLowerCase();
                          const isDuplicate =
                            nameKey.length > 0 &&
                            selectedScannedItems.filter(
                              si =>
                                si.suggestedDisplayName.trim().toLowerCase() ===
                                nameKey,
                            ).length > 1;
                          const isDuplicateSlug =
                            slugKey.length > 0 &&
                            selectedScannedItems.filter(
                              si =>
                                si.suggestedSlug.trim().toLowerCase() ===
                                slugKey,
                            ).length > 1;
                          const isInvalidSlug =
                            item.suggestedSlug.trim().length === 0 ||
                            !NAME_RE.test(item.suggestedSlug.trim());
                          const helperText =
                            item.suggestedDisplayName.trim().length === 0
                              ? 'Display name is required'
                              : isDuplicate
                                ? `${selectedMeta.label} with the same name already exists`
                                : isDuplicateSlug
                                  ? `Slug "${item.suggestedSlug}" conflicts with another selected item`
                                  : isInvalidSlug
                                    ? 'Slug must match ^[a-z0-9][a-z0-9-]{0,62}$'
                                    : `Slug: ${item.suggestedSlug}`;
                          return (
                            <Box
                              style={{
                                marginTop: 18,
                                paddingTop: 16,
                                borderTop: '1px solid #f0f0f0',
                                display: 'grid',
                                gap: 16,
                              }}
                            >
                              <TextField
                                label="Display name"
                                variant="outlined"
                                size="small"
                                value={item.suggestedDisplayName}
                                onChange={e => {
                                  const nextDisplayName = e.target.value;
                                  updateScannedItem(originalIndex, {
                                    suggestedDisplayName: nextDisplayName,
                                    suggestedSlug: nextDisplayName.trim()
                                      ? normalizeSlug(nextDisplayName)
                                      : item.suggestedSlug,
                                  });
                                }}
                                error={
                                  isDuplicate ||
                                  isDuplicateSlug ||
                                  isInvalidSlug ||
                                  item.suggestedDisplayName.trim().length === 0
                                }
                                helperText={helperText}
                              />
                              <TextField
                                label="Description"
                                variant="outlined"
                                size="medium"
                                multiline
                                minRows={2}
                                value={item.description}
                                disabled
                              />
                              <TextField
                                label="Keywords (comma separated)"
                                variant="outlined"
                                size="medium"
                                value={item.keywords.join(', ')}
                                disabled
                              />
                              <TextField
                                label="Version"
                                variant="outlined"
                                size="medium"
                                value={item.version}
                                disabled
                              />
                            </Box>
                          );
                        })()}
                    </Box>
                  );
                })}
              </Box>
            )}

            {!scanning && !scanError && !hasScannedResults && (
              <Typography
                className={classes.helperText}
                style={{ marginTop: 16 }}
              >
                Preparing to scan…
              </Typography>
            )}

            {showGlobalMetadataFields && (
              <>
                <Typography className={classes.fieldLabel}>
                  Description
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="What does this item do?"
                  error={!descriptionValid && description.length > 0}
                  className={classes.field}
                />
                <Typography className={classes.charCount}>
                  {description.length}/200 (aim for 80–120)
                </Typography>

                <Typography className={classes.fieldLabel}>Keywords</Typography>
                <Box className={classes.keywordsWrap}>
                  {keywords.map(kw => (
                    <Chip
                      key={kw}
                      label={kw}
                      size="small"
                      className={classes.keywordChip}
                      onDelete={() =>
                        setKeywords(prev => prev.filter(k => k !== kw))
                      }
                    />
                  ))}
                  <input
                    className={classes.keywordInput}
                    value={keywordDraft}
                    onChange={e => setKeywordDraft(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    placeholder={
                      keywords.length === 0
                        ? 'Type keyword and press Enter'
                        : ''
                    }
                  />
                </Box>

                <Typography className={classes.fieldLabel}>Version</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  value={version}
                  onChange={e => setVersion(e.target.value)}
                  placeholder="1.0.0"
                  className={classes.field}
                  style={{ width: 140 }}
                />
              </>
            )}
          </>
        )}

        {/* ── Step 3: Review ── */}
        {phase === 'step3' && (
          <>
          <Box className={classes.reviewCard}>
            {isScanSelectionMode ? (
              <>
                <Box
                  className={classes.reviewCardHeader}
                  style={{ background: selectedMeta.gradient }}
                >
                  <Typography style={{ color: '#fff', fontWeight: 700 }}>
                    Contributing {selectedScannedItems.length}{' '}
                    {selectedMeta.label}(s) from {repoUrl}
                  </Typography>
                </Box>
                <Box className={classes.reviewCardBody}>
                  {selectedScannedItems.map((item, idx) => (
                    <Box
                      key={`${item.path}-${idx}`}
                      style={{
                        marginBottom: 12,
                        paddingBottom: 10,
                        borderBottom:
                          idx < selectedScannedItems.length - 1
                            ? '1px solid #dfe3e8'
                            : 'none',
                      }}
                    >
                      <Typography
                        style={{
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <TypeIcon
                          iconName={selectedMeta.iconName}
                          fontSize="small"
                          style={{ color: selectedMeta.color }}
                        />{' '}
                        {item.suggestedDisplayName}
                      </Typography>
                      <Typography style={{ fontSize: '0.82rem' }}>
                        {item.description}
                      </Typography>
                      <Typography className={classes.helperText}>
                        Source: {item.path}
                      </Typography>
                      <Typography className={classes.helperText}>
                        Version: {item.version}
                      </Typography>
                      {item.keywords.length > 0 && (
                        <Box className={classes.reviewKeywords}>
                          {item.keywords.map(kw => (
                            <Chip
                              key={`${item.path}-${kw}`}
                              label={kw}
                              size="small"
                              className={classes.keywordChip}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <>
                <Box
                  className={classes.reviewCardHeader}
                  style={{ background: selectedMeta.gradient }}
                >
                  <TypeIcon
                    iconName={selectedMeta.iconName}
                    style={{ color: '#fff', fontSize: '1.4rem' }}
                  />
                  <Box>
                    <Typography
                      style={{
                        fontWeight: 700,
                        color: '#fff',
                        fontSize: '0.95rem',
                      }}
                    >
                      {manualDerivedDisplayName}
                    </Typography>
                    <Typography
                      style={{
                        color: 'rgba(255,255,255,0.75)',
                        fontSize: '0.75rem',
                      }}
                    >
                      {selectedMeta.label}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.reviewCardBody}>
                  {description && (
                    <Box className={classes.reviewRow}>
                      <Typography className={classes.reviewLabel}>
                        Description
                      </Typography>
                      <Typography style={{ fontSize: '0.82rem' }}>
                        {description}
                      </Typography>
                    </Box>
                  )}
                  <Box className={classes.reviewRow}>
                    <Typography className={classes.reviewLabel}>
                      Version
                    </Typography>
                    <Typography style={{ fontSize: '0.82rem' }}>
                      {version}
                    </Typography>
                  </Box>
                  <Box className={classes.reviewRow}>
                    <Typography className={classes.reviewLabel}>
                      Source
                    </Typography>
                  </Box>
                  <Box className={classes.reviewFiles}>
                    {contentMethod === 'link' ? repoUrl : uploadedFileName}
                  </Box>
                  {keywords.length > 0 && (
                    <Box className={classes.reviewKeywords}>
                      {keywords.map(kw => (
                        <Chip
                          key={kw}
                          label={kw}
                          size="small"
                          className={classes.keywordChip}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>

          {/* ── Token Efficiency Analysis Panel ── */}
          {!tokenAnalysisSkipped && (
            <Box className={classes.tePanel}>

              {/* ── Header ── */}
              <Box className={`${classes.tePanelHeader} ${
                tokenAnalysisLoading ? classes.tePanelHeaderLoading
                  : tokenAnalysis?.isTokenEfficient ? classes.tePanelHeaderPass
                  : tokenAnalysis ? classes.tePanelHeaderFail
                  : classes.tePanelHeaderLoading
              }`}>
                <SpeedIcon style={{
                  fontSize: 17,
                  color: tokenAnalysis?.isTokenEfficient ? '#09A761'
                    : tokenAnalysis ? '#C62828'
                    : undefined,
                  opacity: tokenAnalysisLoading ? 0.35 : 1,
                }} />
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1 }}>
                    Token Efficiency Analysis
                  </Typography>
                  {tokenAnalysisLoading && (
                    <Typography style={{ fontSize: '0.7rem', color: 'inherit', opacity: 0.6, marginTop: 2 }}>
                      Analyzing content…
                    </Typography>
                  )}
                  {!tokenAnalysisLoading && tokenAnalysisError && (
                    <Typography style={{ fontSize: '0.7rem', color: 'inherit', opacity: 0.6, marginTop: 2 }}>
                      Analysis unavailable
                    </Typography>
                  )}
                </Box>
                {tokenAnalysisLoading && (
                  <LinearProgress style={{ width: 72, height: 2, borderRadius: 1, opacity: 0.5 }} />
                )}
                {tokenAnalysis && (
                  <Chip
                    size="small"
                    label={tokenAnalysis.isTokenEfficient ? 'Token-Efficient' : 'Needs improvement'}
                    style={{
                      height: 20,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      backgroundColor: tokenAnalysis.isTokenEfficient
                        ? 'rgba(9,167,97,0.12)'
                        : 'rgba(198,40,40,0.08)',
                      color: tokenAnalysis.isTokenEfficient ? '#09A761' : '#C62828',
                      border: `1px solid ${tokenAnalysis.isTokenEfficient ? 'rgba(9,167,97,0.2)' : 'rgba(198,40,40,0.15)'}`,
                    }}
                  />
                )}
              </Box>

              {/* ── Score + Metric Grid ── */}
              {tokenAnalysis && (
                <Box className={classes.tePanelBody}>
                  <Box className={classes.teScoreArea}>
                    {/* Score circle */}
                    <Box
                      className={classes.teScoreCircle}
                      style={{
                        borderColor: tokenAnalysis.isTokenEfficient ? '#09A761' : '#C62828',
                        background: tokenAnalysis.isTokenEfficient
                          ? 'rgba(9,167,97,0.07)'
                          : 'rgba(198,40,40,0.05)',
                      }}
                    >
                      <span
                        className={classes.teScoreNumber}
                        style={{ color: tokenAnalysis.isTokenEfficient ? '#09A761' : '#C62828' }}
                      >
                        {tokenAnalysis.score}
                      </span>
                      <span
                        className={classes.teScoreLabel}
                        style={{ color: tokenAnalysis.isTokenEfficient ? '#09A761' : '#C62828', opacity: 0.7 }}
                      >
                        /100
                      </span>
                    </Box>

                    {/* 5-dimension mini-grid */}
                    <Box className={classes.teMetricGrid}>
                      {[
                        { key: 'Prompt',     val: tokenAnalysis.breakdown.promptScore },
                        { key: 'Output',     val: tokenAnalysis.breakdown.outputConstraintScore },
                        { key: 'Redundancy', val: tokenAnalysis.breakdown.redundancyScore },
                        { key: 'Structure',  val: tokenAnalysis.breakdown.structureScore },
                        { key: 'Focus',      val: tokenAnalysis.breakdown.focusScore },
                      ].map(({ key, val }) => (
                        <Box key={key} className={classes.teMetricCell}>
                          <span
                            className={classes.teMetricValue}
                            style={{ color: val >= 70 ? '#09A761' : val >= 50 ? '#E65100' : '#C62828' }}
                          >
                            {val}
                          </span>
                          <span className={classes.teMetricName}>{key}</span>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* ── Issues ── */}
                  {tokenAnalysis.issues.length > 0 && (
                    <Box style={{ marginBottom: tokenAnalysis.suggestions.length > 0 ? 16 : 0 }}>
                      <Typography className={classes.teSectionTitle}>
                        Issues detected
                      </Typography>
                      {tokenAnalysis.issues.map((issue, i) => (
                        <Box key={i} className={classes.teListItem}>
                          <ErrorOutlineIcon style={{ fontSize: 15, color: '#C62828', marginTop: 1, flexShrink: 0 }} />
                          <Typography className={classes.teListText}>{issue}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* ── Suggestions ── */}
                  {tokenAnalysis.suggestions.length > 0 && (
                    <Box>
                      <Typography className={classes.teSectionTitle}>
                        How to improve
                      </Typography>
                      {tokenAnalysis.suggestions.map((s, i) => (
                        <Box key={i} className={classes.teListItem}>
                          <InfoOutlinedIcon style={{ fontSize: 15, color: '#1565C0', marginTop: 1, flexShrink: 0 }} />
                          <Typography className={classes.teListSubText}>{s}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* ── Footer note ── */}
                  <Box className={`${classes.teFooterNote} ${
                    tokenAnalysis.isTokenEfficient ? classes.teFooterPass : classes.teFooterWarn
                  }`}>
                    {tokenAnalysis.isTokenEfficient
                      ? <CheckCircleIcon style={{ fontSize: 14, flexShrink: 0 }} />
                      : <InfoOutlinedIcon style={{ fontSize: 14, flexShrink: 0 }} />
                    }
                    <span>
                      {tokenAnalysis.isTokenEfficient
                        ? `This item will display the Token-Efficient badge in the marketplace (score: ${tokenAnalysis.score}/100, ~${tokenAnalysis.promptTokens} tokens).`
                        : `Threshold is 70/100. You can still submit — this feedback is informational (score: ${tokenAnalysis.score}/100, ~${tokenAnalysis.promptTokens} tokens).`
                      }
                    </span>
                  </Box>
                </Box>
              )}

              {/* ── Error / Unavailable state ── */}
              {!tokenAnalysis && !tokenAnalysisLoading && tokenAnalysisError && (
                <Box className={classes.tePanelBody}>
                  <Typography style={{ fontSize: '0.8rem' }} color="textSecondary">
                    Token efficiency analysis could not be completed. You can still submit — the score will be computed when the PR is created.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          </>
        )}

        {/* ── Submitting ── */}
        {phase === 'submitting' && (
          <Box style={{ textAlign: 'center', padding: '32px 0' }}>
            <LinearProgress style={{ marginBottom: 16 }} />
            <Typography color="textSecondary" style={{ fontSize: '0.9rem' }}>
              Creating pull request…
            </Typography>
          </Box>
        )}

        {/* ── Success ── */}
        {phase === 'success' && (
          <Box style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircleOutlineIcon
              style={{ fontSize: '2rem', color: '#09A761', marginBottom: 8 }}
            />
            <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
              Pull Request Created!
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textTransform: 'none' }}
            >
              View Pull Request →
            </Button>
          </Box>
        )}

        {/* ── Error ── */}
        {phase === 'error' &&
          (() => {
            const ssoMatch = submitError?.match(
              /visit\s+(https:\/\/github\.com\/enterprises\/[^\s]+)/i,
            );
            const ssoUrl = ssoMatch?.[1];
            const isTokenError =
              /resource not accessible by personal access token/i.test(
                submitError ?? '',
              );
            return (
              <Box style={{ textAlign: 'center', padding: '24px 0' }}>
                <ErrorOutlineIcon
                  style={{
                    fontSize: '2rem',
                    color: '#D64C00',
                    marginBottom: 8,
                  }}
                />
                <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                  Submission Failed
                </Typography>
                {ssoUrl ? (
                  <>
                    <Typography
                      style={{
                        fontSize: '0.85rem',
                        color: '#666',
                        maxWidth: 420,
                        margin: '0 auto 12px',
                      }}
                    >
                      The GitHub token requires SSO authorization. Click below
                      to authorize, then try again.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      href={ssoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 10,
                      }}
                    >
                      Authorize SSO Access ↗
                    </Button>
                  </>
                ) : isTokenError ? (
                  <Typography
                    style={{
                      fontSize: '0.85rem',
                      color: '#666',
                      maxWidth: 440,
                      margin: '0 auto',
                    }}
                  >
                    The server's GitHub token doesn't have write access to the
                    marketplace repository. A portal administrator needs to
                    update the token permissions. Your submission data has been
                    preserved — click <strong>Try Again</strong> after the token
                    is fixed.
                  </Typography>
                ) : (
                  <Typography
                    style={{
                      fontSize: '0.85rem',
                      color: '#c0392b',
                      wordBreak: 'break-word',
                    }}
                  >
                    {submitError}
                  </Typography>
                )}
              </Box>
            );
          })()}
      </DialogContent>

      {/* Footer */}
      <DialogActions className={classes.actions}>
        {phase === 'step1' && (
          <>
            <Button className={classes.cancelBtn} onClick={resetAndClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryBtn}
              onClick={() => setPhase('step2')}
            >
              Next →
            </Button>
          </>
        )}
        {phase === 'step2' && (
          <>
            <Button
              className={classes.cancelBtn}
              onClick={isEditMode ? resetAndClose : () => setPhase('step1')}
            >
              {isEditMode ? 'Cancel' : '← Back'}
            </Button>
            {isEditMode ? (
              <Button
                variant="contained"
                color="primary"
                className={classes.primaryBtn}
                disabled={!step2Valid}
                onClick={handleSubmit}
              >
                Submit Edit
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                className={classes.primaryBtn}
                disabled={hasScanStep ? !hasValidRepoUrl : !step2Valid}
                onClick={() => setPhase(hasScanStep ? 'step2b' : 'step3')}
              >
                {hasScanStep ? 'Next →' : 'Review →'}
              </Button>
            )}
          </>
        )}
        {phase === 'step2b' && (
          <>
            <Button
              className={classes.cancelBtn}
              onClick={() => setPhase('step2')}
            >
              ← Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryBtn}
              disabled={!canReview}
              onClick={() => setPhase('step3')}
            >
              Review →
            </Button>
          </>
        )}
        {phase === 'step3' && (
          <>
            <Button
              className={classes.cancelBtn}
              onClick={() => setPhase(hasScanStep ? 'step2b' : 'step2')}
            >
              ← Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryBtn}
              onClick={handleSubmit}
            >
              Submit Pull Request
            </Button>
          </>
        )}
        {phase === 'success' && (
          <Button className={classes.cancelBtn} onClick={resetAndClose}>
            Close
          </Button>
        )}
        {phase === 'error' && (
          <>
            <Button
              className={classes.cancelBtn}
              onClick={() => setPhase('step3')}
            >
              ← Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryBtn}
              onClick={handleSubmit}
            >
              Try Again
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
